import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Activity, ShieldCheck, Zap, Waves } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { EditorialMarquee } from "@/components/site/Marquee";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal, MaskLine } from "@/components/site/Reveal";

const HERO_IMG = "/hero-product.png?v=2";
const TEX_IMG = "https://static.prod-images.emergentagent.com/jobs/985943a1-da20-48a8-8068-987a05536a80/images/f58d65d5ac3265a6c1cdd7f39198f6e30cdb5042a4c5a93540f3d04198ca0282.jpeg";

export default function Home() {
    const { t } = useLang();
    const [products, setProducts] = useState([]);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    useEffect(() => {
        api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const stats = [
        { icon: Activity, k: "150 Hz", v: t.stats.freq },
        { icon: ShieldCheck, k: "FDA", v: t.stats.cleared },
        { icon: Zap, k: "20 hrs", v: t.stats.battery },
        { icon: Waves, k: "4 ch", v: t.stats.clinical },
    ];

    return (
        <div className="bg-background">
            <Header />

            {/* HERO */}
            <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
                            className="eyebrow text-cyan mb-8 flex items-center gap-3"
                        >
                            <span className="w-8 h-px bg-cyan" /> {t.hero.eyebrow}
                        </motion.div>
                        <h1 className="font-heading font-black text-obsidian tracking-tighter leading-[0.86] text-[13vw] sm:text-[9vw] lg:text-[6.6vw]">
                            <MaskLine delay={0.05}>{t.hero.lines[0]}</MaskLine>
                            <MaskLine delay={0.16} className="text-cyan">{t.hero.lines[1]}</MaskLine>
                            <MaskLine delay={0.27}>{t.hero.lines[2]}</MaskLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
                            className="mt-8 max-w-md text-muted-foreground text-lg leading-relaxed"
                        >
                            {t.hero.sub}
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.8 }}
                            className="mt-10 flex flex-wrap items-center gap-4"
                        >
                            <Link to="/products" data-testid="hero-explore-btn" className="group eyebrow inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-7 py-4 hover:-translate-y-0.5 hover:bg-cyan hover:text-obsidian transition-all duration-300">
                                {t.cta.explore} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/about" data-testid="hero-learn-btn" className="eyebrow inline-flex items-center gap-2 text-obsidian border-b border-obsidian/20 pb-1 hover:border-cyan hover:text-cyan transition-colors">
                                {t.cta.howItWorks}
                            </Link>
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="relative rounded-2xl overflow-hidden border border-border bg-secondary shadow-[0_40px_80px_-30px_rgba(6,182,212,0.35)]"
                        >
                            <motion.img style={{ y: imgY, scale: imgScale }} src={HERO_IMG} alt="Pulse Line device" className="w-full h-[560px] object-contain bg-white p-3" />
                            <div className="absolute bottom-4 left-4 glass rounded-lg px-4 py-3">
                                <div className="eyebrow text-cyan">{t.hero.flagship}</div>
                                <div className="font-heading font-bold text-obsidian">Pulse Line</div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <EditorialMarquee />

            {/* MANIFESTO CHAPTERS */}
            <section className="relative bg-obsidian text-white grain overflow-hidden py-28">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal className="eyebrow text-cyan mb-16">{t.manifesto.eyebrow}</Reveal>
                    <div className="space-y-2">
                        {t.manifesto.chapters.map((c, i) => (
                            <Reveal key={i} delay={i * 0.05}>
                                <div className="group grid lg:grid-cols-12 gap-6 items-start py-10 border-t border-white/10 hover:border-cyan/40 transition-colors duration-500">
                                    <div className="lg:col-span-2 font-mono-tech text-cyan text-lg">{String(i + 1).padStart(2, "0")}</div>
                                    <h3 className="lg:col-span-5 font-heading font-black text-3xl sm:text-4xl tracking-tight leading-[0.95] group-hover:translate-x-2 transition-transform duration-500">{c.t}</h3>
                                    <p className="lg:col-span-5 text-white/60 leading-relaxed text-lg">{c.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
                <img src={TEX_IMG} alt="" className="absolute -right-20 -bottom-20 w-[440px] opacity-20 rotate-6 pointer-events-none select-none" />
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
                    <div>
                        <Reveal className="eyebrow text-cyan mb-4">{t.featured.eyebrow}</Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-obsidian leading-[0.95]">
                                {t.featured.title.split("\n").map((l, i) => <span key={i} className="block">{l}</span>)}
                            </h2>
                        </Reveal>
                    </div>
                    <Link to="/products" data-testid="view-all-products" className="eyebrow inline-flex items-center gap-2 text-obsidian hover:text-cyan transition-colors group">
                        {t.cta.viewAll} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.06}><ProductCard product={p} index={i} /></Reveal>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}
